// Oturumlar sayfası ve üst bant aynı onayı açar: iki kopya, idempotency
// anahtarının yaşam döngüsünü iki ayrı yerde tutmak olurdu.

import { useId } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UserX } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "@novemai/components";

import { showErrorToast } from "@/lib/error-toast";
import {
  useFrozenRetryableSubmission,
} from "@/lib/idempotency-intent";
import {
  endImpersonation,
  IMPERSONATIONS_QUERY_KEY,
  type EndImpersonationInput,
} from "@/lib/impersonation-queries";

import type { ImpersonationSession } from "./StartImpersonationModal";

type EndImpersonationPayload = Pick<EndImpersonationInput, "sessionId">;

export function EndImpersonationModal({
  session,
  onClose,
}: {
  readonly session: ImpersonationSession;
  readonly onClose: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const titleId = useId();
  const retryableSubmission = useFrozenRetryableSubmission<EndImpersonationPayload>();

  const mutation = useMutation({
    mutationFn: endImpersonation,
    onSuccess: (): void => {
      retryableSubmission.clear();
      void queryClient.invalidateQueries({ queryKey: IMPERSONATIONS_QUERY_KEY });
      onClose();
    },
    onError: (error): void => {
      retryableSubmission.settleFailure(error);
      showErrorToast(error, t);
    },
  });

  const submit = (): void => {
    if (mutation.isPending) return;
    const submission =
      retryableSubmission.resume() ??
      retryableSubmission.reserve({ sessionId: session.session_id });
    mutation.mutate({
      sessionId: submission.payload.sessionId,
      idempotencyKey: submission.idempotencyKey,
    });
  };

  const close = (): void => {
    if (retryableSubmission.locked || mutation.isPending) return;
    onClose();
  };

  return (
    <Modal
      open
      onClose={close}
      aria-labelledby={titleId}
      closeOnEscape={!retryableSubmission.locked && !mutation.isPending}
      closeOnScrimClick={!retryableSubmission.locked && !mutation.isPending}
    >
      <ModalHeader id={titleId} title={t("impersonations.end.title")} />
      <ModalBody>
        <p className="mb-3 mt-0 font-serif text-[13.5px] leading-relaxed text-[var(--fg-secondary)]">
          {t("impersonations.end.body")}
        </p>
        <pre className="mb-0 overflow-auto whitespace-pre-wrap break-all rounded-[8px] border border-[var(--border-subtle)] bg-[var(--bg-subtle)] p-3 font-mono text-[11px] leading-relaxed text-[var(--fg-primary)]">
          {session.session_id}
        </pre>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="secondary"
          onClick={close}
          disabled={retryableSubmission.locked || mutation.isPending}
        >
          {t("common.cancel")}
        </Button>
        <Button variant="danger" onClick={submit} loading={mutation.isPending}>
          <UserX size={14} aria-hidden="true" />
          {t("impersonations.end.confirm")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
